import React, { FC, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Copy from 'Src/components/Copy';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { TerminalOption } from 'Src/feature/provision/provisionService';
import { useProvisionerTerminal } from 'Src/feature/provision/useProvisionerTerminal';

interface TerminalClaimedDialogProps {
  terminal: TerminalOption | undefined;
  onCancel: () => void;
  onClaimKilled: () => void;
  open: boolean;
}

const TerminalClaimedDialog: FC<TerminalClaimedDialogProps> = ({
  open,
  terminal,
  onClaimKilled,
  onCancel,
}) => {
  const { t } = useTranslation();

  const killProvisionClaim = useProvisionerTerminal.mutate.killProvisionClaim();

  const [isOpen, isOpenSet] = useState(open);
  const [isKillingClaim, isKillingClaimSet] = useState(false);

  const handleKillConfirmed = async () => {
    isKillingClaimSet(true);
    try {
      await killProvisionClaim.mutateAsync({ terminalId: terminal?.id as number });
      onClaimKilled();
    } catch (e) {
      console.error('TerminalClaimedDlg.handleKillConfirmed error', e);
    } finally {
      isKillingClaimSet(false);
    }
  };

  // bind incoming props to local state
  useEffect(() => {
    if (open !== isOpen) isOpenSet(open);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      variant="prompt"
      color="warning"
      aria-labelledby="dlg-confirm-title"
      aria-describedby="dlg-confirm-description"
      open={isOpen}
      data-testid="dlg-terminal-claimed"
    >
      <ModalHeader>
        <ModalTitle id="dlg-confirm-title" copy={t('provision.terminalClaimed.labels.deviceClaimed')}/>
      </ModalHeader>

      <ModalContent>
        <Trans
          i18nKey="provision.terminalClaimed.info.deviceCurrentlyClaimed"
          // eslint-disable-next-line react/jsx-key
          components={[<Copy id="dlg-confirm-description" />]}
        />
      </ModalContent>

      <ModalActions>
        <ModalAction
          disabled={isKillingClaim}
          onClick={onCancel}
          label={t('provision.terminalClaimed.labels.cancel')}
          color="secondary"
        />
        <ModalAction
          disabled={isKillingClaim}
          onClick={handleKillConfirmed}
          color="primary"
          label={t('provision.terminalClaimed.labels.release')}
        />
      </ModalActions>
    </Modal>
  );
};

export default TerminalClaimedDialog;
